// Custom hooks for agent/routing data
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Hook to get all agents with their stats combined
export const useAgents = (options = {}) => {
  const goalsQuery = useQuery({
    queryKey: ['agentGoals'],
    queryFn: () => apiClient.getDistinctGoals(),
    staleTime: 1000 * 60 * 5,
    ...options,
  });

  const goals = goalsQuery.data?.goals || [];

  const statsQueries = useQueries({
    queries: goals.map(goal => ({
      queryKey: ['goalStats', goal],
      queryFn: () => apiClient.getGoalStats(goal),
      staleTime: 1000 * 60 * 3,
      enabled: !!goal && goalsQuery.isSuccess,
    })),
  });

  // Add execution count queries as fallback for sample_count
  const execQueries = useQueries({
    queries: goals.map(goal => ({
      queryKey: ['goalExecCount', goal],
      queryFn: () => apiClient.getExecutionsByGoal(goal, 168, 1), // Just get count
      staleTime: 1000 * 60 * 3,
      enabled: !!goal && goalsQuery.isSuccess,
    })),
  });

  // Ensure we wait for ALL stats queries to complete before showing agents
  // This handles the race condition when goals array changes and statsQueries
  // hasn't caught up yet, or when queries are still loading
  // Show agents as soon as goals load, let stats fill in progressively
  const statsQueriesReady = goals.length === 0 || statsQueries.length === goals.length;
  // execQueries are background fallback only - don't block render
  const isLoading = goalsQuery.isLoading || !statsQueriesReady;
  const error = goalsQuery.error || statsQueries.find(q => q.error)?.error;

  const agents = goals.map((goal, i) => {
    const execCount = execQueries[i]?.data?.total || 0;
    const paths = [...(statsQueries[i]?.data?.paths || [])]; // Clone to avoid mutation

    // If paths have no sample_count but we have executions, distribute count
    if (execCount > 0 && paths.length > 0 && paths.every(p => !p.sample_count)) {
      const perPath = Math.floor(execCount / paths.length);
      paths.forEach(p => {
        p.sample_count = perPath;
      });
    }

    return {
      goal,
      paths,
      stats: statsQueries[i]?.data || {},
      execCount,
      lastActivity: statsQueries[i]?.data?.last_activity,
    };
  });

  return {
    data: { agents },
    isLoading,
    error,
  };
};

// Hook to get all goals (agents)
export const useAgentGoals = (options = {}) => {
  return useQuery({
    queryKey: ['agentGoals'],
    queryFn: () => apiClient.getDistinctGoals(),
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

// Hook to get detailed stats for a goal (with traffic distribution)
export const useGoalStats = (goal, options = {}) => {
  return useQuery({
    queryKey: ['goalStats', goal],
    queryFn: () => apiClient.getGoalStats(goal),
    staleTime: 1000 * 30,
    enabled: !!goal,
    ...options,
  });
};

// Hook to get recent outcomes for activity feed
export const useRecentOutcomes = (goal, limit = 20, options = {}) => {
  return useQuery({
    queryKey: ['recentOutcomes', goal, limit],
    queryFn: () => apiClient.getRecentOutcomes(goal, limit),
    staleTime: 1000 * 15,
    enabled: !!goal,
    ...options,
  });
};

// Hook to get exploration config for a goal
export const useAgentConfig = (goal, options = {}) => {
  return useQuery({
    queryKey: ['agentConfig', goal],
    queryFn: () => apiClient.getExplorationConfig(goal),
    staleTime: 1000 * 60,
    enabled: !!goal,
    ...options,
  });
};

// Hook to get executions/traces for a goal
export const useGoalExecutions = (goal, hours = 168, limit = 50, options = {}) => {
  return useQuery({
    queryKey: ['goalExecutions', goal, hours, limit],
    queryFn: () => apiClient.getExecutionsByGoal(goal, hours, limit),
    staleTime: 1000 * 30,
    enabled: !!goal,
    ...options,
  });
};

// Mutation to update exploration config
export const useUpdateAgentConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => apiClient.setExplorationConfig(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agentConfig'] });
      queryClient.invalidateQueries({ queryKey: ['goalStats'] });
    },
  });
};

// Hook to get execution tree (spans) for a specific trace
export const useExecutionSpans = (traceId, options = {}) => {
  return useQuery({
    queryKey: ['executionSpans', traceId],
    queryFn: () => apiClient.getExecutionTree(traceId),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !!traceId && options.enabled !== false,
    ...options,
  });
};
